import { useState } from 'react';
import { normalizeHeader } from './formHeader';
import { renameConditionAnswer } from './formConditions';
import { cleanPresetOptions, withSavedPreset, withoutPreset } from './optionPresets';
import {
  appendField,
  appendFieldToSection,
  findEntry,
  insertFieldAt,
  insertSectionAt,
  mapEntryById,
  moveEntry,
  newSection,
  removeEntryById,
  removeSectionOnly,
  renameSection,
  stepEntry,
} from './formSections';

let fieldSequence = 0;

// Ids only need to be unique within a form, but two questions added in the same
// millisecond would collide on Date.now() alone — hence the counter.
const newBlankField = () => {
  fieldSequence += 1;
  return {
    id: `field_${Date.now()}_${fieldSequence}`,
    label: '',
    name: '',
    type: 'text',
    required: false,
    presets: [],
  };
};

const emptySchema = () => ({
  fields: [],
  calculations: [],
  header: normalizeHeader(null),
  optionPresets: [],
  hiddenPresetIds: [],
});

// Owns the form schema being edited and all the field-mutation operations used
// by FormBuilder. Returns the schema plus a stable set of mutators. The "add"
// mutators return the new entry's id so the caller can open or focus that card.
export const useFormSchema = () => {
  const [schema, setSchema] = useState(emptySchema);

  const addField = () => {
    const field = newBlankField();
    setSchema((prev) => ({ ...prev, fields: appendField(prev.fields, field) }));
    return field.id;
  };

  // Renaming a field never touches calculations: expressions reference fields
  // by id, so a question can be retitled freely without rewriting anything.
  const updateField = (fieldId, updates) => {
    setSchema((prev) => {
      const field = findEntry(prev.fields, fieldId);
      if (!field) return prev;
      const isTypeChangeToSelect = updates.type === 'select' && field.type !== 'select';
      const fieldUpdates = isTypeChangeToSelect ? { ...updates, options: field.options || [] } : updates;

      return {
        ...prev,
        fields: mapEntryById(prev.fields, fieldId, (f) => ({ ...f, ...fieldUpdates })),
      };
    });
  };

  const updateHeader = (updates) => {
    setSchema((prev) => ({
      ...prev,
      header: normalizeHeader({ ...normalizeHeader(prev.header), ...updates }),
    }));
  };

  // Every question edit below reaches its question wherever it lives — at the
  // top level or inside a section at any depth — through mapEntryById. A list
  // left untouched is handed back as-is, so a no-op edit doesn't churn the
  // schema.
  const updateFieldList = (fieldId, key, updater) => {
    setSchema((prev) => ({
      ...prev,
      fields: mapEntryById(prev.fields, fieldId, (field) => {
        const current = Array.isArray(field[key]) ? field[key] : [];
        const next = updater(current);
        return next === current ? field : { ...field, [key]: next };
      }),
    }));
  };

  const addSelectOption = (fieldId) =>
    updateFieldList(fieldId, 'options', (options) => [...options, '']);

  // A condition that chose this option follows it as it's retyped, so fixing a
  // typo in "Yes" doesn't strand the questions waiting on it. Unless another
  // option still reads the old way: then the condition still names a real
  // answer, and stays put.
  const updateSelectOption = (fieldId, index, value) => {
    setSchema((prev) => {
      const field = findEntry(prev.fields, fieldId);
      const options = Array.isArray(field?.options) ? field.options : [];
      const previous = options[index];
      let fields = mapEntryById(prev.fields, fieldId, (f) => ({
        ...f,
        options: options.map((option, idx) => (idx === index ? value : option)),
      }));
      const stillOffered = options.some((option, idx) => idx !== index && option === previous);
      if (previous && previous !== value && !stillOffered) {
        fields = renameConditionAnswer(fields, fieldId, previous, value);
      }
      return { ...prev, fields };
    });
  };

  const removeSelectOption = (fieldId, index) =>
    updateFieldList(fieldId, 'options', (options) => options.filter((_, idx) => idx !== index));

  const addPresetOption = (fieldId) =>
    updateFieldList(fieldId, 'presets', (presets) => [...presets, '']);

  const updatePresetOption = (fieldId, index, value) =>
    updateFieldList(fieldId, 'presets', (presets) =>
      presets.map((preset, idx) => (idx === index ? value : preset)));

  const removePresetOption = (fieldId, index) =>
    updateFieldList(fieldId, 'presets', (presets) => presets.filter((_, idx) => idx !== index));

  // Lift an option out of the list and drop it back in at `toIndex`, the same
  // splice a drag does to the questions themselves.
  const moveSelectOptionTo = (fieldId, fromIndex, toIndex) =>
    updateFieldList(fieldId, 'options', (options) => {
      if (
        fromIndex === toIndex
        || fromIndex < 0
        || toIndex < 0
        || fromIndex >= options.length
        || toIndex >= options.length
      ) {
        return options;
      }
      const next = [...options];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });

  // Replace a Dropdown's options with a saved set, in the preset's order.
  const applyOptionPreset = (fieldId, options) => {
    const applied = cleanPresetOptions(options);
    setSchema((prev) => ({
      ...prev,
      fields: mapEntryById(prev.fields, fieldId, (field) => ({ ...field, options: applied })),
    }));
  };

  const saveOptionPreset = (name, options) => {
    setSchema((prev) => withSavedPreset(prev, name, options));
  };

  const deleteOptionPreset = (presetId) => {
    setSchema((prev) => withoutPreset(prev, presetId));
  };

  const removeField = (fieldId) => {
    setSchema((prev) => ({ ...prev, fields: removeEntryById(prev.fields, fieldId) }));
  };

  // A drag drops a card at a position named relative to another card; the
  // keyboard walks it one slot at a time. What either means with sections in
  // the way is decided in formSections.js.
  const moveEntryTo = (entryId, target) => {
    setSchema((prev) => {
      const fields = moveEntry(prev.fields, entryId, target);
      return fields === prev.fields ? prev : { ...prev, fields };
    });
  };

  const stepEntryBy = (entryId, direction) => {
    setSchema((prev) => {
      const fields = stepEntry(prev.fields, entryId, direction);
      return fields === prev.fields ? prev : { ...prev, fields };
    });
  };

  // A question started in a gap between cards lands in that gap, in whichever
  // container holds it — the form itself, or a section at any depth.
  const addFieldAt = (containerId, index) => {
    const field = newBlankField();
    setSchema((prev) => ({
      ...prev,
      fields: insertFieldAt(prev.fields, containerId, index, field),
    }));
    return field.id;
  };

  const addFieldToSection = (sectionId) => {
    const field = newBlankField();
    setSchema((prev) => ({ ...prev, fields: appendFieldToSection(prev.fields, sectionId, field) }));
    return field.id;
  };

  // A new section takes the questions that follow it, in whichever container it
  // was started — the form itself, or a section, where it becomes a subsection.
  // See insertSectionAt.
  const addSection = (containerId, index) => {
    const section = newSection();
    setSchema((prev) => ({
      ...prev,
      fields: insertSectionAt(prev.fields, containerId, index, section),
    }));
    return section.id;
  };

  const updateSectionName = (sectionId, name) => {
    setSchema((prev) => ({ ...prev, fields: renameSection(prev.fields, sectionId, name) }));
  };

  // Set the condition on a question or a section, or take it off with null —
  // unticking Conditional removes it outright rather than leaving a switched-off
  // one behind in the saved form. See formConditions.js for its shape.
  const setCondition = (entryId, condition) => {
    setSchema((prev) => ({
      ...prev,
      fields: mapEntryById(prev.fields, entryId, (entry) => {
        if (condition) return { ...entry, condition };
        const { condition: removed, ...rest } = entry;
        return rest;
      }),
    }));
  };

  // `keepFields` is the difference between deleting a heading and deleting a
  // chapter: without it everything inside — questions and subsections — goes
  // too.
  const removeSection = (sectionId, { keepFields } = {}) => {
    setSchema((prev) => ({
      ...prev,
      fields: keepFields
        ? removeSectionOnly(prev.fields, sectionId)
        : removeEntryById(prev.fields, sectionId),
    }));
  };

  return {
    schema,
    setSchema,
    addField,
    updateField,
    updateHeader,
    addSelectOption,
    updateSelectOption,
    removeSelectOption,
    moveSelectOptionTo,
    applyOptionPreset,
    saveOptionPreset,
    deleteOptionPreset,
    addPresetOption,
    updatePresetOption,
    removePresetOption,
    removeField,
    moveEntryTo,
    stepEntryBy,
    addFieldAt,
    addFieldToSection,
    addSection,
    updateSectionName,
    setCondition,
    removeSection,
  };
};

export default useFormSchema;
